import { Upload } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, hlaskaZChyby } from '../../api/client.js';
import type { ImportBatch, Smlouva, Zakazka } from '../../api/types.js';
import { useAuth } from '../../auth/AuthContext.js';
import { formatDatum } from '../../lib/format.js';
import { SMLOUVA_TYP_LABEL } from '../../lib/labels.js';
import { Badge, Btn, Chyba, EmptyState, Karta, Nacitani, td, th } from '../../components/ui.js';
import { ImportWizard } from './ImportWizard.js';
import { CenikPolozkyPanel } from './CenikPolozky.js';

/**
 * Import soupisu prací — seznam batchů (platný/archivní) + spuštění wizardu.
 * Reimport = nový batch; starý zůstává archivovaný (verzovaný read-only ceník).
 */
export function ImportPage() {
  const { maPravo } = useAuth();
  const queryClient = useQueryClient();
  // Deep-link z detailu smlouvy (sekce Ceník): ?novy=1&zakazka=…&smlouva=…
  // otevře wizard rovnou s předvyplněnou zakázkou a smlouvou.
  const [hledani, setHledani] = useSearchParams();
  const [wizard, setWizard] = useState(hledani.get('novy') === '1');
  const initialZakazkaId = hledani.get('zakazka') ?? '';
  const initialSmlouvaId = hledani.get('smlouva') ?? '';
  // Klik na batch otevře obsah ceníku (položky) pod tabulkou.
  const [otevrenyBatchId, setOtevrenyBatchId] = useState<string | null>(null);

  const {
    data: batche,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['importy'],
    queryFn: () => api.get<ImportBatch[]>('/api/imports'),
    enabled: !wizard,
  });
  const { data: zakazky } = useQuery({
    queryKey: ['zakazky'],
    queryFn: () => api.get<Zakazka[]>('/api/projects'),
    enabled: maPravo('projekty.read'),
  });
  const { data: smlouvy } = useQuery({
    queryKey: ['smlouvy'],
    queryFn: () => api.get<Smlouva[]>('/api/contracts'),
    enabled: maPravo('smlouvy.read'),
  });

  if (wizard) {
    return (
      <ImportWizard
        initialZakazkaId={initialZakazkaId}
        initialSmlouvaId={initialSmlouvaId}
        onHotovo={() => {
          setWizard(false);
          if (hledani.has('novy')) setHledani({}, { replace: true });
          void queryClient.invalidateQueries({ queryKey: ['importy'] });
          void queryClient.invalidateQueries({ queryKey: ['zakazky'] });
        }}
      />
    );
  }

  const nazevZakazky = (id: string) => {
    const z = zakazky?.find((z) => z.id === id);
    return z ? `${z.cislo} — ${z.nazev}` : '—';
  };
  const nazevSmlouvy = (id: string | null) => {
    if (!id) return '—';
    const s = smlouvy?.find((s) => s.id === id);
    return s ? `${SMLOUVA_TYP_LABEL[s.typ]}${s.cislo ? ` ${s.cislo}` : ''}` : '—';
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Soupis prací (import)</h1>
        {maPravo('import.write') && <Btn onClick={() => setWizard(true)}>Nový import</Btn>}
      </div>

      <Karta>
        {isLoading && <Nacitani />}
        {error ? <Chyba>{hlaskaZChyby(error)}</Chyba> : null}
        {batche && batche.length === 0 && (
          <EmptyState
            ikona={<Upload size={22} strokeWidth={1.75} />}
            titulek="Zatím žádný import"
            popis="Nahrajte oceněný soupis prací (.xlsx) — vznikne read-only ceník, proti kterému se oceňují změnové listy."
            akce={
              maPravo('import.write') ? (
                <Btn onClick={() => setWizard(true)}>Nový import</Btn>
              ) : undefined
            }
          />
        )}
        {batche && batche.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className={th}>Soubor</th>
                  <th className={th}>Zakázka</th>
                  <th className={th}>Smlouva</th>
                  <th className={th}>Kdy</th>
                  <th className={th}>Kdo</th>
                  <th className={th}>Položek</th>
                  <th className={th}>Verze</th>
                </tr>
              </thead>
              <tbody>
                {batche.map((b) => (
                  <tr
                    key={b.id}
                    className={`cursor-pointer ${
                      b.id === otevrenyBatchId
                        ? 'bg-primary-tint'
                        : b.jePlatny
                          ? 'hover:bg-bg'
                          : 'text-text-secondary hover:bg-bg'
                    }`}
                    onClick={() => setOtevrenyBatchId(otevrenyBatchId === b.id ? null : b.id)}
                  >
                    <td className={`${td} font-semibold`}>{b.nazevSouboru}</td>
                    <td className={td}>{nazevZakazky(b.zakazkaId)}</td>
                    <td className={td}>{nazevSmlouvy(b.smlouvaId)}</td>
                    <td className={td}>{formatDatum(b.kdy)}</td>
                    <td className={td}>{b.kdo.jmeno}</td>
                    <td className={td}>{b._count.polozky}</td>
                    <td className={td}>
                      <Badge stav={b.jePlatny ? 'success' : 'neutral'}>
                        {b.jePlatny ? 'platný' : 'archivní'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Karta>

      {/* Obsah vybraného ceníku — klik na řádek výše (nejen metadata souboru). */}
      {otevrenyBatchId && <CenikPolozkyPanel batchId={otevrenyBatchId} />}
    </>
  );
}
